import type { IBracketStore, IdentifiableVault, LoadedVault, LoadedVaultWithPnl, ManagedVault } from "@types";
import { getLoadedVault, getManagedVault, } from "."; 




/**
 * Refreshes the data for a vault based on its current state.
 * 
 * @template T - The type of vault state to refresh (either "LOADED" or "MANAGEABLE")
 * @param {IdentifiableVault} vault - The vault to refresh data for
 * @returns {Promise<LoadMap[T]>} - A promise that resolves to the refreshed vault data
 * @throws {Error} - If the vault state is unknown
 */
export const refreshVaultData = async <T extends keyof LoadMap>( store: IBracketStore, vault: LoadedVault, ): Promise<LoadMap[T]> => {
  switch(vault.state) {
    case "LOADED":
      return { ...await getLoadedVault(store, vault, true, )} as LoadMap[T];
    case "MANAGEABLE":
      return { ...await getManagedVault(store, vault, true, )} as LoadMap[T];
    default:
      throw new Error(`unknown vault state: ${vault.state}`);
  }
}


/**
 * Type guard to check if a vault is in the LOADED state.
 * 
 * @param {IdentifiableVault} vault - The vault to check
 * @returns {vault is LoadedVault} - Type predicate indicating if the vault is in LOADED state
 */
export const isLoadedVault = (vault: IdentifiableVault): vault is LoadedVault => {
  return "state" in vault && vault.state === "LOADED";
}


/**
 * Type guard to check if a vault is in the MANAGEABLE state.
 * 
 * @param {IdentifiableVault} vault - The vault to check
 * @returns {vault is ManagedVault} - Type predicate indicating if the vault is in MANAGEABLE state
 */
export const isManagedVault = (vault: IdentifiableVault): vault is ManagedVault => {
  return "state" in vault && vault.state === "MANAGEABLE";
}


type LoadMap = {
  LOADED: LoadedVaultWithPnl;
  MANAGEABLE: ManagedVault;
}
